"use client";

import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";

export type PitchPricingRow = {
  timeKey: string;
  regular: number;
  vip10: number;
  vip15: number;
};

export type PitchPricingSectionProps = {
  rows: PitchPricingRow[];
  /** Already localized subtitle under the title */
  pricingSubtitle: string;
};

export default function PitchPricingSection({ rows, pricingSubtitle }: PitchPricingSectionProps) {
  const { t, i18n } = useTranslation();

  const formatPrice = (price: number) =>
    new Intl.NumberFormat(i18n.language?.startsWith("es") ? "es-AR" : "en-US", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(price);

  return (
    <section id="pricing" className="mt-16 w-full scroll-mt-24">
      <div className="mb-8 max-w-2xl">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          {t("pricingPage.title")}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">{pricingSubtitle}</p>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th
                scope="col"
                className="bg-slate-50/90 py-3.5 pl-0 pr-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                {t("pricingPage.table.time")}
              </th>
              <th
                scope="col"
                className="bg-slate-50/90 px-3 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                {t("pricingPage.table.regular")}
              </th>
              <th
                scope="col"
                className="bg-slate-50/90 px-3 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-primary"
              >
                {t("pricingPage.table.vip10")}
              </th>
              <th
                scope="col"
                className="bg-slate-50/90 py-3.5 pl-3 pr-0 text-right text-xs font-semibold uppercase tracking-wider text-emerald-700"
              >
                {t("pricingPage.table.vip15")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={row.timeKey + idx}
                className="border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50/60"
              >
                <td className="max-w-[11rem] py-3.5 pl-0 pr-4 text-slate-700">
                  {t(row.timeKey).replace("hs", "")}
                </td>
                <td className="px-3 py-3.5 text-right font-semibold tabular-nums text-slate-900">
                  {formatPrice(row.regular)}
                </td>
                <td className="px-3 py-3.5 text-right font-semibold tabular-nums text-primary">
                  {formatPrice(row.vip10)}
                </td>
                <td className="py-3.5 pl-3 pr-0 text-right font-semibold tabular-nums text-emerald-700">
                  {formatPrice(row.vip15)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-10 grid gap-8 border-t border-slate-200 pt-10 sm:grid-cols-2 sm:gap-x-12 sm:gap-y-0">
        <div className="flex gap-3.5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Info className="h-4 w-4 text-primary" aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">{t("pricingPage.table.vip10")}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
              {t("pricingPage.table.vip10Desc")}
            </p>
          </div>
        </div>
        <div className="flex gap-3.5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10">
            <Info className="h-4 w-4 text-emerald-700" aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">{t("pricingPage.table.vip15")}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
              {t("pricingPage.table.vip15Desc")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
